import { AuthProvider, UserIdentity } from 'react-admin';
import sso from '@/lib/sso';
import { syncAuthToLocalStorage, clearSsoAuthData } from '@/utils/authSync';
import { getCurrentUser, getCurrentRole } from '@/utils/auth';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://portal.roommatik.com';

export const authProvider: AuthProvider = {
  login: async (): Promise<void> => {
    // Handled by Keycloak — this is a no-op
  },

  logout: async (): Promise<void> => {
    clearSsoAuthData();
    sso.logout({ redirectUri: PORTAL_URL });
    // Never resolves — browser navigates away
    return new Promise(() => {});
  },

  checkAuth: async (): Promise<void> => {
    if (!sso.authenticated) {
      return Promise.reject({ message: 'Not authenticated', redirectTo: false });
    }
    try {
      const refreshed = await sso.updateToken(30);
      if (refreshed) syncAuthToLocalStorage(sso);
    } catch {
      clearSsoAuthData();
      window.location.href = `${PORTAL_URL}?app=kyc`;
      return Promise.reject({ redirectTo: false });
    }
  },

  checkError: async (error): Promise<void> => {
    const status = error?.status || error?.response?.status;
    if (status === 401 || status === 403) {
      try {
        await sso.updateToken(30);
        syncAuthToLocalStorage(sso);
      } catch {
        clearSsoAuthData();
        window.location.href = `${PORTAL_URL}?app=kyc`;
        return Promise.reject({ redirectTo: false });
      }
    }
  },

  getIdentity: async (): Promise<UserIdentity> => {
    const user = getCurrentUser();
    if (!user) throw new Error('No user found');
    return {
      id: user.id,
      fullName: user.fullName,
      avatar: undefined,
      email: user.email,
      role: user.role,
    } as UserIdentity & { email: string; role: string };
  },

  getPermissions: async (): Promise<string> => {
    const role = getCurrentRole();
    if (!role) throw new Error('No role found');
    return role;
  },
};

export default authProvider;
